import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';


export const sendAnalyticsEvents = createAsyncThunk(
  'analytics/sendEvents',
  async (events, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/activity/analytics/events', 
        { events });
      return {
        success: true,
        processed: events.length,
        message: response.data.message || 'Events sent successfully',
        data: response.data
      };
      
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to send analytics events'
      );
    }
  }
);

export const fetchUserActivities = createAsyncThunk(
  'analytics/fetchUserActivities',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });

      const response = await apiClient.get(
        `/activity/my-activity?${queryParams.toString()}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch user activities'
      );
    }
  }
);

export const fetchFinancialSummary = createAsyncThunk(
  'analytics/fetchFinancialSummary',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/activity/financial-summary?days=${days}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch financial summary'
      );
    }
  }
);

export const fetchModuleUsage = createAsyncThunk(
  'analytics/fetchModuleUsage',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/activity/module-usage?days=${days}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch module usage'
      );
    }
  }
);

export const fetchEngagementMetrics = createAsyncThunk(
  'analytics/fetchEngagementMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/activity/engagement-metrics');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch engagement metrics'
      );
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState: {
    eventQueue: [],
    eventsSent: 0,
    eventsError: 0,
    
    userActivities: {
      data: [],
      total: 0,
      hasMore: false,
      loading: false,
      error: null
    },
    
    financialSummary: {
      data: null,
      loading: false,
      error: null
    },
    
    moduleUsage: {
      data: null,
      loading: false,
      error: null
    },
    
    engagementMetrics: {
      data: null,
      loading: false,
      error: null
    },
    
    // Session data
    sessionData: {
      sessionId: null,
      startTime: null,
      lastActivity: null,
      pageViews: 0,
      interactionCount: 0
    },
    
    loading: false,
    error: null
  },
  reducers: {
    addEventToQueue: (state, action) => {
      state.eventQueue.push(action.payload);
    },
    
    clearEventQueue: (state) => {
      state.eventQueue = [];
    },
    
    initializeSession: (state, action) => {
      state.sessionData = {
        sessionId: action.payload.sessionId,
        startTime: action.payload.startTime,
        lastActivity: Date.now(),
        pageViews: 0,
        interactionCount: 0
      };
    },
    
    updateLastActivity: (state) => {
      state.sessionData.lastActivity = Date.now();
    },
    
    incrementPageViews: (state) => {
      state.sessionData.pageViews += 1;
    },
    
    incrementInteractions: (state) => {
      state.sessionData.interactionCount += 1;
    },
    
    clearAnalyticsError: (state) => {
      state.error = null;
      state.userActivities.error = null;
      state.financialSummary.error = null;
      state.moduleUsage.error = null;
      state.engagementMetrics.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAnalyticsEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendAnalyticsEvents.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.success) {
          state.eventsSent += state.eventQueue.length;
          state.eventQueue = [];
        }
      })
      .addCase(sendAnalyticsEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to send events';
        if (action.payload?.canRetry) {
          state.eventsError += state.eventQueue.length;
        }
      })
      
      .addCase(fetchUserActivities.pending, (state) => {
        state.userActivities.loading = true;
        state.userActivities.error = null;
      })
      .addCase(fetchUserActivities.fulfilled, (state, action) => {
        state.userActivities.loading = false;
        state.userActivities.data = action.payload.activities;
        state.userActivities.total = action.payload.total;
        state.userActivities.hasMore = action.payload.hasMore;
      })
      .addCase(fetchUserActivities.rejected, (state, action) => {
        state.userActivities.loading = false;
        state.userActivities.error = action.payload;
      })
      
      .addCase(fetchFinancialSummary.pending, (state) => {
        state.financialSummary.loading = true;
        state.financialSummary.error = null;
      })
      .addCase(fetchFinancialSummary.fulfilled, (state, action) => {
        state.financialSummary.loading = false;
        state.financialSummary.data = action.payload;
      })
      .addCase(fetchFinancialSummary.rejected, (state, action) => {
        state.financialSummary.loading = false;
        state.financialSummary.error = action.payload;
      })
      .addCase(fetchModuleUsage.pending, (state) => {
        state.moduleUsage.loading = true;
        state.moduleUsage.error = null;
      })
      .addCase(fetchModuleUsage.fulfilled, (state, action) => {
        state.moduleUsage.loading = false;
        state.moduleUsage.data = action.payload;
      })
      .addCase(fetchModuleUsage.rejected, (state, action) => {
        state.moduleUsage.loading = false;
        state.moduleUsage.error = action.payload;
      })
      
      .addCase(fetchEngagementMetrics.pending, (state) => {
        state.engagementMetrics.loading = true;
        state.engagementMetrics.error = null;
      })
      .addCase(fetchEngagementMetrics.fulfilled, (state, action) => {
        state.engagementMetrics.loading = false;
        state.engagementMetrics.data = action.payload;
      })
      .addCase(fetchEngagementMetrics.rejected, (state, action) => {
        state.engagementMetrics.loading = false;
        state.engagementMetrics.error = action.payload;
      });
  }
});

export const selectAnalyticsLoading = (state) => state.analytics.loading;
export const selectAnalyticsError = (state) => state.analytics.error;
export const selectEventQueue = (state) => state.analytics.eventQueue;
export const selectEventsSent = (state) => state.analytics.eventsSent;
export const selectEventsError = (state) => state.analytics.eventsError;
export const selectSessionData = (state) => state.analytics?.sessionData || {};

export const selectUserActivities = (state) => state.analytics.userActivities.data;
export const selectUserActivitiesLoading = (state) => state.analytics.userActivities.loading;
export const selectUserActivitiesError = (state) => state.analytics.userActivities.error;

export const selectFinancialSummary = (state) => state.analytics.financialSummary.data;
export const selectFinancialSummaryLoading = (state) => state.analytics.financialSummary.loading;

export const selectModuleUsage = (state) => state.analytics.moduleUsage.data;
export const selectModuleUsageLoading = (state) => state.analytics.moduleUsage.loading;

export const selectEngagementMetrics = (state) => state.analytics.engagementMetrics.data;
export const selectEngagementMetricsLoading = (state) => state.analytics.engagementMetrics.loading;

export const {
  addEventToQueue,
  clearEventQueue,
  initializeSession,
  updateLastActivity,
  incrementPageViews,
  incrementInteractions,
  clearAnalyticsError
} = analyticsSlice.actions;

export default analyticsSlice.reducer;